import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const Feedback: React.FC = () => {
  const { user } = useAuth();
  const [type, setType] = useState<'bug' | 'feature'>('feature');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const isGlobalUser = user?.company_id == null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!title.trim()) {
      setError('Please enter a title.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/portal-issues', { type, title: title.trim(), description: description.trim() || undefined });
      setSubmitted(true);
      setTitle('');
      setDescription('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 border border-green-200 dark:border-green-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Thank you</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Your {type === 'bug' ? 'bug report' : 'feature request'} has been submitted. We’ll review it and update the tracker when appropriate.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => { setSubmitted(false); }}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              Submit another
            </button>
            <Link
              to="/my-feedback"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand hover:bg-brand-hover"
            >
              View my feedback
            </Link>
            {isGlobalUser && (
              <Link
                to="/portal-issues"
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                View all bugs & feature requests
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">Request a feature or report a bug</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Describe what you’d like to see or what went wrong. Submissions are visible to portal admins and global users.
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
            <div className="flex gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="feature"
                  checked={type === 'feature'}
                  onChange={() => setType('feature')}
                  className="rounded border-gray-300 text-brand focus:ring-brand"
                />
                <span className="ml-2 text-gray-700 dark:text-gray-300">Feature request</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="type"
                  value="bug"
                  checked={type === 'bug'}
                  onChange={() => setType('bug')}
                  className="rounded border-gray-300 text-brand focus:ring-brand"
                />
                <span className="ml-2 text-gray-700 dark:text-gray-300">Bug report</span>
              </label>
            </div>
          </div>
          <div>
            <label htmlFor="feedback-title" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
            <input
              id="feedback-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={type === 'bug' ? 'Short description of the bug' : 'Short description of the feature'}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
              maxLength={255}
            />
          </div>
          <div>
            <label htmlFor="feedback-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description (optional)</label>
            <textarea
              id="feedback-description"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add more context, steps to reproduce, or desired behavior..."
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-brand focus:ring-brand sm:text-sm"
            />
          </div>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand hover:bg-brand-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </button>
            {isGlobalUser && (
              <Link
                to="/portal-issues"
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                View tracker
              </Link>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Feedback;
